import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { MapPin, Play, Square, AlertCircle } from "lucide-react";

export default function Deliver() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [active, setActive] = useState(false);
  const [position, setPosition] = useState(null);
  const [error, setError] = useState("");
  const [lastSent, setLastSent] = useState(null);
  const [sendCount, setSendCount] = useState(0);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const latestPosRef = useRef(null);

  const send = async (lat, lng) => {
    try {
      await api.post(`/orders/${orderId}/location`, { lat, lng, token });
      setLastSent(new Date());
      setSendCount((c) => c + 1);
      setError("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to send location");
    }
  };

  const start = () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported on this device");
      return;
    }
    setActive(true);
    setError("");

    // Continuous watcher
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy };
        latestPosRef.current = p;
        setPosition(p);
      },
      (err) => {
        setError(err.message || "Could not get location");
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    // Push to server every 15 seconds
    intervalRef.current = setInterval(() => {
      const p = latestPosRef.current;
      if (p) send(p.lat, p.lng);
    }, 15000);

    // Send first update immediately when we get a fix
    const firstFix = setInterval(() => {
      if (latestPosRef.current) {
        send(latestPosRef.current.lat, latestPosRef.current.lng);
        clearInterval(firstFix);
      }
    }, 1000);
  };

  const stop = () => {
    setActive(false);
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="deliver-page" className="bg-[#fbf8f1] min-h-screen">
      <div className="max-w-md mx-auto px-4 py-10">
        <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold">
          SVRM · Delivery
        </div>
        <h1 className="text-2xl font-bold text-stone-900 mt-1">
          Share live location
        </h1>
        <p className="text-stone-600 text-sm mt-2">
          Tap <span className="font-semibold">Start sharing</span> below. Your phone's location
          will be sent to the customer's tracking page every 15 seconds.
        </p>

        <div className="mt-6 bg-white border border-stone-200 rounded-sm p-5">
          <div className="text-xs text-stone-500 mb-1">Order</div>
          <div className="font-mono text-sm">{orderId}</div>
        </div>

        {error && (
          <div
            data-testid="deliver-error"
            className="mt-4 p-4 bg-red-50 border border-red-200 rounded-sm flex items-start gap-2 text-sm text-red-800"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}

        {position && (
          <div className="mt-4 bg-white border border-stone-200 rounded-sm p-5">
            <div className="flex items-center gap-2 text-stone-900">
              <MapPin size={16} className="text-[#274e13]" />
              <span className="font-mono text-xs">
                {position.lat.toFixed(5)}, {position.lng.toFixed(5)}
              </span>
            </div>
            {position.acc && (
              <div className="text-xs text-stone-500 mt-1">
                Accuracy: ±{Math.round(position.acc)} m
              </div>
            )}
            <div className="text-xs text-stone-500 mt-1">
              Updates sent: <span className="font-semibold">{sendCount}</span>
              {lastSent && ` · last ${lastSent.toLocaleTimeString()}`}
            </div>
          </div>
        )}

        <div className="mt-6">
          {!active ? (
            <button
              data-testid="deliver-start"
              onClick={start}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#274e13] hover:bg-[#38761d] text-white py-4 rounded-sm font-semibold tracking-wide transition-colors"
            >
              <Play size={16} /> Start sharing location
            </button>
          ) : (
            <button
              data-testid="deliver-stop"
              onClick={stop}
              className="w-full inline-flex items-center justify-center gap-2 bg-red-700 hover:bg-red-800 text-white py-4 rounded-sm font-semibold tracking-wide transition-colors"
            >
              <Square size={14} /> Stop sharing
            </button>
          )}
        </div>

        <div className="mt-8 text-xs text-stone-500 leading-relaxed">
          <p className="font-semibold text-stone-700 mb-1">Tips</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Keep this tab open in your browser while delivering</li>
            <li>Best on mobile data with GPS enabled (not just Wi-Fi)</li>
            <li>Allow "Location" permission when prompted</li>
            <li>Tap Stop when delivery is complete</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
